/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import * as Yup from 'yup';

import { Box } from '@mui/material';

import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { AuthenticationApiService } from '../../services/AuthenticationApiService';
import { ApiClientError } from '../../types/api/ApiClientError';
import { UnprocessableEntityPayload } from '../../types/api/UnprocessableEntityPayload';
import { unprocessibleEntityToObject } from '../../utils/unprocessibleEntityToObject';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useObjectState } from '../../utils/useObjectState';
import { ForgotForm } from './components/ForgotForm/ForgotForm';
import { createForgotPageStyles } from './ForgotPage.styles';
import { ForgotApiValidationErrors, ForgotPageFormValues, ResetApiErrorForField } from './ForgotPage.types';

const validationSchema = Yup.object({
  email: Yup.string().email('invalid_email').required('empty'),
});

type State = {
  isFetching: boolean;
  errorMessage?: string;
  apiErrors: ForgotApiValidationErrors;
};

export const ForgotPage = memo(() => {
  const { t } = useTranslation(Namespaces.Login);
  useDocumentTitle(t(`${Namespaces.Common}:page_titles.forgot_password`));
  const classes = createForgotPageStyles();
  const [state, setState] = useObjectState<State>({
    apiErrors: {},
    isFetching: false,
  });
  const { isFetching, apiErrors, errorMessage } = state;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = useMemo(() => searchParams.get('email') ?? '', [searchParams]);

  const submit = async (data: ForgotPageFormValues) => {
    try {
      setState({ isFetching: true, apiErrors: {}, errorMessage: undefined });
      await AuthenticationApiService.forgot(data.email.trim());
      void navigate(BasicRoutes.CONFIRM_PASSWORD_RESET);
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      if (error.response?.data.message === 'unprocessable_entity') {
        const payload: UnprocessableEntityPayload<keyof ForgotPageFormValues> = error.response.data.payload;
        setState({
          isFetching: false,
          apiErrors: unprocessibleEntityToObject(payload),
        });
      } else {
        setState({
          isFetching: false,
          errorMessage: error.response?.data.message,
        });
      }
    }
  };

  const resetApiError: ResetApiErrorForField = name => {
    if (apiErrors[name]) {
      setState({ apiErrors: { ...apiErrors, [name]: undefined } });
    }
  };

  return (
    <>
      <Box className={classes.formTitle}>{t('login:page.forgot_password.title')}</Box>
      <Formik<ForgotPageFormValues>
        initialValues={{ email: email }}
        validationSchema={validationSchema}
        onSubmit={submit}
      >
        {props => <ForgotForm {...props} isFetching={isFetching} resetApiError={resetApiError} apiErrors={apiErrors} />}
      </Formik>

      <Box className={classes.errorBlock}>
        {errorMessage && <p className={classes.errorLine}>{t(`login:page.login.error.${errorMessage}`)}</p>}
      </Box>
    </>
  );
});
